const STATE_CHANGING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

let cachedCsrfToken: string | null = null;

async function getCsrfToken(): Promise<string> {
  if (cachedCsrfToken) {
    return cachedCsrfToken;
  }
  const response = await fetch("/auth/csrf", { credentials: "include" });
  if (!response.ok) {
    throw new ApiError(response.status, "Unable to obtain a CSRF token.");
  }
  const data = (await response.json()) as { csrfToken: string };
  cachedCsrfToken = data.csrfToken;
  return cachedCsrfToken;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  validationErrors?: string[];

  constructor(status: number, message: string, code?: string, validationErrors?: string[]) {
    super(message);
    this.status = status;
    this.code = code;
    this.validationErrors = validationErrors;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Lets a caller cancel a long request (e.g. the AI assistant). There is no built-in timeout. */
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}, _csrfRetry = false): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = { "Content-Type": "application/json" };

  if (STATE_CHANGING_METHODS.has(method)) {
    headers["X-CSRF-Token"] = await getCsrfToken();
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: "include",
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await response.json().catch(() => null);

  // CSRF token expired/rotated server-side: fetch a fresh one and retry once.
  if (response.status === 403 && STATE_CHANGING_METHODS.has(method) && !_csrfRetry && /csrf/i.test(payload?.error?.message ?? "")) {
    resetCsrfCache();
    return apiRequest<T>(path, options, true);
  }

  if (!response.ok) {
    // FastAPI's HTTPException(detail={"errors": [...]}) shape (used for
    // report-definition validation failures) vs. our standard
    // {"error": {code, message}} envelope used everywhere else.
    const validationErrors: string[] | undefined = Array.isArray(payload?.error?.errors)
      ? payload.error.errors
      : Array.isArray(payload?.detail?.errors)
        ? payload.detail.errors
        : undefined;

    const message =
      payload?.error?.message ??
      (typeof payload?.detail === "string" ? payload.detail : undefined) ??
      (validationErrors ? validationErrors.join("; ") : undefined) ??
      `Request to ${path} failed.`;

    throw new ApiError(response.status, message, payload?.error?.code, validationErrors);
  }

  return payload as T;
}

export function resetCsrfCache(): void {
  cachedCsrfToken = null;
}

/** Multipart upload (e.g. Power BI files) with the same CSRF/credentials
 * handling and error shape as apiRequest. */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "X-CSRF-Token": await getCsrfToken() },
    credentials: "include",
    body: form,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const errors: string[] | undefined = Array.isArray(payload?.error?.errors) ? payload.error.errors : Array.isArray(payload?.detail?.errors) ? payload.detail.errors : undefined;
    throw new ApiError(response.status, payload?.error?.message ?? (typeof payload?.detail === "string" ? payload.detail : `Upload to ${path} failed.`), payload?.error?.code, errors);
  }
  return payload as T;
}
